import {
  NOTIFICATION_ALERT_KINDS,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_STATUSES,
} from '@platform/contracts';
import type { BadgeTone } from '@platform/ui';

/**
 * The words the notification screens put on screen, and the keys they read
 * (MASTER_PLAN **M9.8**, FR-ADMIN-08, FR-NOTIF-01…09, `Docs/API.md` §2.9).
 *
 * Every *shape* comes from `@platform/contracts`; what lives here is the English for a
 * machine name and the permission keys, named once so the screens, the nav and the tests
 * cannot drift apart by a typo — a mistyped key fails **closed**, which means a control
 * silently disappears for everyone and nothing points at why (rules §17.2, §22.2).
 */

export { NOTIFICATION_ALERT_KINDS, NOTIFICATION_CHANNELS, NOTIFICATION_STATUSES };

/** Spelled exactly as `SECURITY.md` §5.1 spells them. */
export const NOTIFICATION_PERMISSIONS = {
  read: 'notifications.read',
  resend: 'notifications.resend',
  templatesManage: 'templates.manage',
  settingsManage: 'notification_settings.manage',
} as const;

/** How many delivery rows one page of the log carries. The rest is behind the cursor. */
export const NOTIFICATION_PAGE_SIZE = 25;

/**
 * Plain English for a template key.
 *
 * The API answers with a `name` for every template, so this is a fallback and a filter
 * label rather than the primary source — but the delivery log has only the `kind`, and
 * `admin_charge_failed` is not a sentence anybody should have to read in a table.
 */
export const NOTIFICATION_KIND_LABELS: Readonly<Record<string, string>> = {
  booking_confirmation: 'Booking confirmation',
  reminder_24h: 'Reminder — 24 hours',
  reminder_30m: 'Reminder — 30 minutes',
  reschedule: 'Reschedule',
  cancellation: 'Cancellation',
  receipt: 'Receipt',
  fee_applied: 'Fee applied',
  waitlist_offer: 'Waitlist offer',
  waitlist_offer_closed: 'Waitlist offer closed',
  intake_reminder: 'Intake reminder',
  admin_new_booking: 'Staff alert — new booking',
  admin_reschedule: 'Staff alert — reschedule',
  admin_cancellation: 'Staff alert — cancellation',
  admin_charge_failed: 'Staff alert — failed charge',
  admin_integration_failed: 'Staff alert — integration failure',
  admin_review_task: 'Staff alert — review task',
  admin_delivery_complaint: 'Staff alert — delivery complaint',
  staff_invitation: 'Staff invitation',
};

export function kindLabel(kind: string): string {
  return NOTIFICATION_KIND_LABELS[kind] ?? kind.replace(/_/g, ' ');
}

/** The seven events a clinic can route (FR-NOTIF-02, Q-P07). */
export const ALERT_KIND_LABELS: Readonly<Record<string, string>> = {
  new_booking: 'New booking',
  reschedule: 'Reschedule',
  cancellation: 'Cancellation',
  charge_failed: 'Failed charge',
  integration_failed: 'Integration failure',
  review_task: 'Review task',
  delivery_complaint: 'Delivery complaint',
};

/** What each alert is actually for, so a clinic can tell two of them apart. */
export const ALERT_KIND_HELP: Readonly<Record<string, string>> = {
  new_booking: 'Somebody booked a visit.',
  reschedule: 'A patient moved a visit to another time.',
  cancellation: 'A patient cancelled a visit.',
  charge_failed: 'A card was declined. Nobody else is told — the patient cannot act on it.',
  integration_failed: 'A connected system stopped answering.',
  review_task: 'Something needs a human decision, such as an insurance check.',
  delivery_complaint: 'Somebody marked one of your emails as spam.',
};

export const NOTIFICATION_STATUS_LABELS: Readonly<Record<string, string>> = {
  scheduled: 'Scheduled',
  queued: 'Queued',
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Failed',
  suppressed: 'Suppressed',
  /* Only a fallback — the filter's `<option>`. Every *row* is labelled by
     `ehrSentLabel`, which can name the clinic's own EHR. */
  suppressed_ehr: 'Sent by your EHR',
  cancelled: 'Cancelled',
};

/**
 * What a `suppressed_ehr` row says (M9.14, FR-NOTIF-10).
 *
 * Not "Suppressed": `suppressed` is a provider refusing a message — a bounced address, an
 * opted-out number — and it is a contact a member of staff can go and correct. This is the
 * opposite fact. The clinic told us their EHR owns that kind, so nothing was composed and
 * nothing failed, and the only honest sentence is the one the clinic can act on
 * (`packages/contracts/src/notifications.ts`, `RUNBOOK.md` §10E).
 *
 * The name is the clinic's own `ehrName`. "your EHR" is the fallback rather than "Tebra"
 * because the setting takes any name and guessing the vendor would be this screen
 * inventing a fact about somebody's practice.
 */
export function ehrSentLabel(ehrName: string | null | undefined): string {
  const name = ehrName?.trim();
  return `Sent by ${name === undefined || name === '' ? 'your EHR' : name}`;
}

/**
 * The one line the drawer adds under an EHR-owned row.
 *
 * "Delivery not tracked here" is the whole point of it: Tebra exposes no delivery state at
 * all (`_research/tebra-communications.md` §1.7), so an empty event timeline on one of
 * these rows is not a bug and must not read as one.
 */
export function ehrSentExplanation(ehrName: string | null | undefined): string {
  const name = ehrName?.trim();
  const who = name === undefined || name === '' ? 'your EHR' : name;
  return `This clinic has handed this message to ${who}, so nothing was sent from here and there is nothing to send again. ${who} does not report delivery back, so it cannot be tracked on this screen.`;
}

export const NOTIFICATION_EVENT_LABELS: Readonly<Record<string, string>> = {
  accepted: 'Accepted by the provider',
  delivered: 'Delivered',
  temporary_fail: 'Temporary failure — will retry',
  permanent_fail: 'Permanent failure',
  complained: 'Marked as spam',
  unsubscribed: 'Unsubscribed',
  queued: 'Queued',
  sent: 'Sent to the carrier',
  undelivered: 'Undelivered by the carrier',
  failed: 'Failed',
  opted_out: 'Opted out (STOP)',
  opted_in: 'Opted back in (START)',
  help_requested: 'Asked for help (HELP)',
  inbound: 'The patient replied',
};

/** The three reminder jobs the queue runs (`reminder_jobs`, FR-NOTIF-04). */
export const REMINDER_KIND_LABELS: Readonly<Record<string, string>> = {
  t24h: '24 hours before',
  t30m: '30 minutes before',
  waitlist_offer: 'Waitlist offer',
};

/**
 * A status is only useful in a table if failure looks different from success at a glance,
 * and colour is never the only signal (rules §48.5): the badge carries the word too.
 */
export function statusTone(status: string): BadgeTone {
  if (status === 'delivered') return 'success';
  if (status === 'sent') return 'info';
  if (status === 'failed') return 'danger';
  /* `suppressed_ehr` is deliberately **not** here: nothing went wrong, so a warning
     colour would be this screen calling the clinic's own configuration a problem. */
  if (status === 'suppressed' || status === 'cancelled') return 'warning';
  return 'neutral';
}

export function eventTone(event: string): BadgeTone {
  if (event === 'delivered' || event === 'opted_in') return 'success';
  if (event === 'permanent_fail' || event === 'failed' || event === 'undelivered') return 'danger';
  if (
    event === 'temporary_fail' ||
    event === 'complained' ||
    event === 'unsubscribed' ||
    event === 'opted_out'
  ) {
    return 'warning';
  }
  return 'neutral';
}
